import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const DiscoveryCall = () => {
  useEffect(() => {
    // Load Calendly widget script
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script on unmount
      const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-cleaning-blue-900 pt-24 pb-16">
      {/* Background Elements */}
      <div className="fixed inset-0 bg-grid-pattern opacity-5 pointer-events-none" />
      <div className="fixed top-20 right-0 w-96 h-96 bg-cleaning-blue-500 rounded-full filter blur-[150px] opacity-20 pointer-events-none" />
      <div className="fixed bottom-0 left-0 w-96 h-96 bg-action-green-500 rounded-full filter blur-[150px] opacity-15 pointer-events-none" />

      <div className="container-custom relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 text-slate-400 hover:text-white mb-8 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </Link>

          <div className="grid lg:grid-cols-5 gap-8 items-start">
            {/* Left Side - Content */}
            <div className="lg:col-span-2">
              <span className="inline-flex items-center gap-2 px-4 py-2 bg-action-green-500/20 text-action-green-400 rounded-full text-sm font-semibold mb-6">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                Free 15-Minute Call
              </span>

              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
                Schedule Your
                <span className="text-action-green-400"> Strategy Session</span>
              </h1>

              <p className="text-lg text-slate-300 mb-8">
                Discuss your service area, volume needs, and how our pre-set commercial walkthroughs can scale your cleaning business.
              </p>

              {/* What to Expect */}
              <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <svg className="w-5 h-5 text-action-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  What to Expect
                </h3>

                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-cleaning-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-cleaning-blue-400 font-bold text-sm">1</span>
                    </div>
                    <div>
                      <p className="font-medium text-white">Quick Capacity Brief</p>
                      <p className="text-sm text-slate-400">We'll assess your current team size and how many new accounts you can handle.</p>
                    </div>
                  </li>

                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-cleaning-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-cleaning-blue-400 font-bold text-sm">2</span>
                    </div>
                    <div>
                      <p className="font-medium text-white">Vetting Process Overview</p>
                      <p className="text-sm text-slate-400">Learn how we qualify decision-makers and set confirmed appointments.</p>
                    </div>
                  </li>

                  <li className="flex items-start gap-3">
                    <div className="w-8 h-8 bg-cleaning-blue-500/20 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-cleaning-blue-400 font-bold text-sm">3</span>
                    </div>
                    <div>
                      <p className="font-medium text-white">Custom Regional Plan</p>
                      <p className="text-sm text-slate-400">Get a tailored strategy for your specific county or service region.</p>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Trust Badge */}
              <div className="mt-6 flex items-center gap-3 text-slate-400">
                <svg className="w-5 h-5 text-action-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
                <span className="text-sm">No commitment required. Just a conversation.</span>
              </div>
            </div>

            {/* Right Side - Calendly Embed */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
                <div 
                  className="calendly-inline-widget" 
                  data-url="https://calendly.com/squeakyleads/discovery-call?hide_gdpr_banner=1&background_color=ffffff&text_color=1e293b&primary_color=22c55e"
                  style={{ minWidth: '320px', height: '700px' }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DiscoveryCall;
